import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import RichTextEditor from '../RichTextEditor';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Save, Plus, Trash2, GripVertical, Type, MapPin, Users, FileText, Image, Eye, TrendingUp, Clock, LayoutGrid } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Dashboard Stats Card Component
const StatCard = ({ icon: Icon, title, value, subtitle, color = 'emerald' }) => {
  const colorClasses = {
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    orange: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    pink: 'bg-pink-500/10 text-pink-400 border-pink-500/20',
    cyan: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  };

  return (
    <div className={`rounded-xl border p-4 ${colorClasses[color]}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color].split(' ')[0]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-zinc-400 text-sm">{title}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
        </div>
      </div>
      {subtitle && <p className="text-xs text-zinc-500 mt-1">{subtitle}</p>}
    </div>
  );
};

// Sortable Section Item Component
const SortableSectionItem = ({ section, updateSection, deleteSection }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style}
      className="bg-zinc-800 rounded-lg p-4 border border-zinc-700"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button 
            {...attributes} 
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-zinc-700 rounded"
          >
            <GripVertical className="w-5 h-5 text-zinc-500" />
          </button>
          {section.type === 'text' ? (
            <Type className="w-5 h-5 text-blue-400" />
          ) : (
            <MapPin className="w-5 h-5 text-emerald-400" />
          )}
          <span className="text-white font-medium">{section.title}</span>
          <Badge className={section.active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
            {section.active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={section.active}
            onCheckedChange={(checked) => updateSection(section.id, 'active', checked)}
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => deleteSection(section.id)}
            className="text-red-400 hover:text-red-300"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="space-y-2">
          <Label className="text-zinc-400 text-sm">Section Title</Label>
          <Input
            value={section.title}
            onChange={(e) => updateSection(section.id, 'title', e.target.value)}
            className="bg-zinc-700 border-zinc-600 text-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-400 text-sm">Type</Label>
          <select
            value={section.type}
            onChange={(e) => updateSection(section.id, 'type', e.target.value)}
            className="w-full bg-zinc-700 border border-zinc-600 text-white rounded-md h-10 px-3"
          >
            <option value="text">Text Content</option>
            <option value="cities">City Links</option>
          </select>
        </div>
      </div>

      {section.type === 'text' ? (
        <div className="space-y-2">
          <Label className="text-zinc-400 text-sm">Content</Label>
          <RichTextEditor
            value={section.content || ''}
            onChange={(content) => updateSection(section.id, 'content', content)}
            placeholder="Write your content here..."
          />
        </div>
      ) : (
        <div className="space-y-2">
          <Label className="text-zinc-400 text-sm">Cities (comma separated)</Label>
          <Textarea
            value={Array.isArray(section.cities) ? section.cities.join(', ') : ''}
            onChange={(e) => updateSection(section.id, 'cities', e.target.value.split(',').map(c => c.trim()).filter(c => c))}
            className="bg-zinc-700 border-zinc-600 text-white"
            placeholder="Istanbul, Ankara, Izmir, ..."
          />
        </div>
      )}
    </div>
  );
};

const HomepageTab = ({
  homepageSections,
  setHomepageSections,
  saving,
  onSave,
  token,
  gridColumns = 6,
  setGridColumns,
  mobileGridColumns = 3,
  setMobileGridColumns,
  itemsPerPage = 24,
  setItemsPerPage,
  onSaveGridColumns
}) => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalListings: 0,
    activeListings: 0,
    pendingListings: 0,
    totalImages: 0,
    totalViews: 0,
    recentActivity: []
  });
  const [loadingStats, setLoadingStats] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchStats();
  }, [token]);

  const fetchStats = async () => {
    try {
      setLoadingStats(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      const [adminStats, uploadStats] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/upload/stats`, { headers }).catch(() => ({ data: { total_files: 0 } }))
      ]);

      setStats({
        totalUsers: adminStats.data.total_users || 0,
        totalListings: adminStats.data.total_listings || 0,
        activeListings: adminStats.data.approved_listings || 0,
        pendingListings: adminStats.data.pending_listings || 0,
        totalImages: uploadStats.data.total_files || 0,
        cdnImages: uploadStats.data.cdn_files || 0,
        totalSizeMB: uploadStats.data.total_size_mb || 0
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setHomepageSections((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        return newItems.map((item, index) => ({ ...item, order: index + 1 }));
      });
    }
  };

  const addSection = () => {
    const newSection = {
      id: `section-${Date.now()}`,
      type: 'text',
      title: 'New Section',
      content: 'Write your content here...',
      order: homepageSections.length + 1,
      active: true
    };
    setHomepageSections([...homepageSections, newSection]);
  };

  const updateSection = (id, field, value) => {
    setHomepageSections(homepageSections.map(s => 
      s.id === id ? { ...s, [field]: value } : s
    ));
  };

  const deleteSection = (id) => {
    setHomepageSections(homepageSections.filter(s => s.id !== id));
  };

  return (
    <div className="space-y-6">
      {/* Dashboard Stats */}
      <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
        <h2 className="text-xl font-semibold text-white mb-4">📊 Quick Statistics</h2>
        
        {loadingStats ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="bg-zinc-800 rounded-xl h-24 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard 
              icon={Users} 
              title="Total Users" 
              value={stats.totalUsers}
              color="blue"
            />
            <StatCard 
              icon={FileText} 
              title="Total Listings" 
              value={stats.totalListings}
              color="purple"
            />
            <StatCard 
              icon={Eye} 
              title="Active Listings" 
              value={stats.activeListings}
              color="emerald"
            />
            <StatCard 
              icon={Clock} 
              title="Pending" 
              value={stats.pendingListings}
              subtitle="Awaiting approval"
              color="orange"
            />
            <StatCard 
              icon={Image} 
              title="Total Images" 
              value={stats.totalImages}
              subtitle={`${stats.cdnImages || 0} CDN'de`}
              color="pink"
            />
            <StatCard 
              icon={TrendingUp} 
              title="Storage" 
              value={`${stats.totalSizeMB?.toFixed(1) || 0} MB`}
              color="cyan"
            />
          </div>
        )}
      </div>

      {/* Grid Columns Setting */}
      <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <LayoutGrid className="w-5 h-5 text-emerald-400" />
            <h2 className="text-xl font-semibold text-white">Listing Grid Layout</h2>
          </div>
          <Button onClick={onSaveGridColumns} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
        {/* Desktop */}
        <p className="text-zinc-400 text-sm mb-3">Desktop - Listings per row</p>
        <div className="flex gap-3 mb-6">
          {[4, 5, 6].map((cols) => (
            <button
              key={cols}
              onClick={() => setGridColumns(cols)}
              className={`flex-1 py-4 rounded-lg border-2 transition-all ${
                gridColumns === cols
                  ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                  : 'border-zinc-700 bg-zinc-800 text-zinc-400 hover:border-zinc-600'
              }`}
            >
              <div className="text-center">
                <div className="flex justify-center gap-1 mb-2">
                  {[...Array(cols)].map((_, i) => (
                    <div key={i} className={`w-4 h-6 rounded-sm ${gridColumns === cols ? 'bg-emerald-500/50' : 'bg-zinc-600'}`} />
                  ))}
                </div>
                <span className="text-lg font-bold">{cols}</span>
                <span className="text-sm ml-1">columns</span>
              </div>
            </button>
          ))}
        </div>

        {/* Mobile */}
        <p className="text-zinc-400 text-sm mb-3">Mobile - Listings per row</p>
        <div className="flex gap-3">
          {[2, 3, 4].map((cols) => (
            <button
              key={cols}
              onClick={() => setMobileGridColumns(cols)}
              className={`flex-1 py-4 rounded-lg border-2 transition-all ${
                mobileGridColumns === cols
                  ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                  : 'border-zinc-700 bg-zinc-800 text-zinc-400 hover:border-zinc-600'
              }`}
            >
              <div className="text-center">
                <div className="flex justify-center gap-1 mb-2">
                  {[...Array(cols)].map((_, i) => (
                    <div key={i} className={`w-3 h-5 rounded-sm ${mobileGridColumns === cols ? 'bg-blue-500/50' : 'bg-zinc-600'}`} />
                  ))}
                </div>
                <span className="text-lg font-bold">{cols}</span>
                <span className="text-sm ml-1">columns</span>
              </div>
            </button>
          ))}
        </div>

        {/* Items Per Page */}
        <div className="mt-6 pt-6 border-t border-zinc-700">
          <p className="text-zinc-400 text-sm mb-3">Listings per page</p>
          <div className="flex flex-wrap gap-2">
            {[12, 24, 36, 48, 60, 80, 100].map((count) => (
              <button
                key={count}
                onClick={() => setItemsPerPage(count)}
                className={`px-4 py-2 rounded-lg border-2 transition-all text-sm font-medium ${
                  itemsPerPage === count
                    ? 'border-amber-500 bg-amber-500/10 text-amber-400'
                    : 'border-zinc-700 bg-zinc-800 text-zinc-400 hover:border-zinc-600'
                }`}
              >
                {count}
              </button>
            ))}
          </div>
          <p className="text-zinc-500 text-xs mt-2">A "Load More" button will appear for remaining listings</p>
        </div>
      </div>

      {/* Homepage Sections */}
      <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Homepage Sections</h2>
          <div className="flex gap-2">
            <Button onClick={addSection} variant="outline" className="border-zinc-700 text-zinc-300">
              <Plus className="w-4 h-4 mr-2" />
              Add Section
            </Button>
            <Button onClick={onSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save All'}
            </Button>
          </div>
        </div>

        <p className="text-zinc-400 text-sm mb-6">
          Manage sections displayed below listings on homepage. Drag to reorder.
        </p>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={homepageSections.map(s => s.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-4">
              {homepageSections.map((section) => (
                <SortableSectionItem
                  key={section.id}
                  section={section}
                  updateSection={updateSection}
                  deleteSection={deleteSection}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {homepageSections.length === 0 && (
          <p className="text-zinc-500 text-center py-8">No sections yet. Click "Add Section" to create one.</p>
        )}
      </div>
    </div>
  );
};

export default HomepageTab;
